import { mapState } from '~/assets/javascript/modules/vuex';

import { VIEW_TYPES } from '~/assets/javascript/constants/types';
import ROUTES from '~/assets/javascript/constants/routes';
import { firstAvailableScopeForView, viewBelongsToScope } from '~/assets/javascript/utils';
import type { View } from '~/types/view';

const loadView = async ({ isPublic = false } = {}) => {
  const rollbar = useRollbar();
  const { $store } = useNuxtApp();
  const route = useRoute();
  const { $i18n, $notifier } = useNuxtApp();

  try {
    const view = await $store.dispatch('view/loadView', { ...route.params, isPublic });

    if (route.name.includes(ROUTES.member.view.record.new.routeName) && !view.permit_record_insert) {
      const message = $i18n.t('global.request.unauthorizedError');
      $notifier.showMessage({ content: message, color: 'warning' });

      return { viewLoadError: message };
    }

    return { view };
  } catch (error: any) {
    let messageKey = 'global.unexpectedError';

    if (error.response) {
      const status = error.response?.status;

      if (status === 404) {
        messageKey = 'global.request.notFoundError';
      } else if ([401, 403].includes(status)) {
        messageKey = 'global.request.unauthorizedError';
      } else {
        rollbar.error('loadView: HttpError', error);
      }
    }

    const message = $i18n.t(messageKey);

    $notifier.showMessage({ content: message, color: 'warning' });

    return { viewLoadError: message };
  }
};

const redirectManagement = ({ isPublic = false } = {}) => {
  const { $store } = useNuxtApp();
  const route = useRoute();

  const { view } = $store.state.view;
  const pageState = VIEW_TYPES[view.page_type].builderOptions.availableDefaultStates.includes(view.page_default_state)
    ? view.page_default_state
    : 'index';

  if (route.query.back && !pageState.includes('only')) return {};

  const { tenantSlug } = route.params;
  const records = $store.getters['recordList/records'];
  const recordCount = records.length;
  const recordId = records[0]?.id;
  const canCreate = $store.getters['view/canCreate'];
  const newPath = { name: 't-tenantSlug-m-viewId-r-new', params: { viewId: view.id, tenantSlug }, query: route.query };
  const publicNewPath = { name: 't-tenantSlug-p-viewId-r-new', params: { viewId: view.id, tenantSlug }, query: route.query };
  const singleItemPath = { name: 't-tenantSlug-m-viewId-r-recordId', params: { viewId: view.id, tenantSlug, recordId }, query: route.query };
  const publicSingleItemPath = { name: 't-tenantSlug-p-viewId-r-recordId', params: { viewId: view.id, tenantSlug, recordId }, query: route.query };

  if (pageState === 'single_item_only') {
    if (recordCount > 0) return { redirectTo: isPublic ? publicSingleItemPath : singleItemPath };
  }

  if (pageState === 'new_only') return { redirectTo: isPublic ? publicNewPath : newPath };

  if (pageState === 'single_item') {
    if (recordCount > 0) return { redirectTo: isPublic ? publicSingleItemPath : singleItemPath };
  }

  if (pageState === 'new') {
    if (canCreate) return { redirectTo: isPublic ? publicNewPath : newPath };
  }

  // Comportamento esperado:
  // Form Default States: index, new, single_item
  // 0 records:           index, new, index
  // 1+ record:           index, new, edit/show
  // !CanCreate:          index, index, edit/show

  // Cards Default States: index, single_item
  // 0 records:            index, index
  // 1+ record:            index, edit/show

  return {};
};

const fetchColumns = (view) => {
  if (_isEmpty(view) || view.page_type === 'Content') return [];
  if (!view.fields_ids || !view.fields) return [];

  return view.fields_ids
    .map(id => view.fields.find(x => x.id === id))
    .filter(Boolean)
    .map(f => ({
      ...f,
      value: f.id,
      text: f.name,
      systemName: f.system_name,
      readOnly: f.read_only,
      width: 200,
    }));
};

const scopeGuard = async ({ $store, route, $auth }) => {
  const currentViewId = route.params?.viewId;
  const { views } = $store.state.viewsMenu;
  const view = views.find(v => v.id === currentViewId);
  const currentScope = $store.getters['workspace/currentScope'];
  const availableScopes = $store.getters['workspace/availableScopes'];

  if (!view || view.category.category_system_name === 'public' || viewBelongsToScope(view, currentScope, $auth.user)) return;
  const scopeToSet = firstAvailableScopeForView(view, availableScopes, $auth.user);

  await $store.dispatch('workspace/updateCurrentScope', { scope: scopeToSet });
};

const getNavigationData = async ({ $store, route, $auth, isPublic }) => {
  await $store.dispatch('viewsMenu/loadViews', { tenantSlug: route.params.tenantSlug, isPublic: Boolean(isPublic) });

  await scopeGuard({ $store, route, $auth });

  await $store.dispatch('workspace/setCategoryByViewId', route.params.viewId);
};

interface ViewFetchParams {
  previewMode?: boolean;
  isPublic?: boolean;
  autoRecordRedirect?: boolean;
  skipBuildHead?: boolean;
}

export async function useViewFetch({ previewMode = false, isPublic, autoRecordRedirect, skipBuildHead }: ViewFetchParams = {}) {
  const nuxtApp = useNuxtApp();
  const { $store, $localePath, $auth, $i18n, $errorRescue } = nuxtApp;

  try {
    const route = useRoute();
    const router = useRouter();

    const { view, viewLoadError } = await loadView({ isPublic });

    if (viewLoadError) return { viewLoadError };

    if (autoRecordRedirect) {
      const { redirectTo } = redirectManagement({ isPublic });

      if (redirectTo) {
        const route = $localePath(redirectTo);

        if (route) {
          await router.replace($localePath(redirectTo));
          return undefined;
        }
      }
    }

    if (!skipBuildHead) buildHead({ title: computed(() => view.page_name) });

    const columns = fetchColumns(view);

    await getNavigationData({ $store, route, $auth, isPublic });

    $store.commit('viewOptions/setIsPreviewMode', previewMode);

    return {
      viewLoadError,
      columns,
    };
  } catch (error) {
    $errorRescue(nuxtApp, error, 'useViewFetch', { onlyReturnErrorMsg: true });

    return {
      viewLoadError: $i18n.t('global.unexpectedError'),
    };
  }
}

export const useView = () => {
  const { $store } = useNuxtApp();
  const route = useRoute();
  const { view } = mapState($store, 'view', ['view']) as { view: ComputedRef<View> };

  const showView = computed(() => view?.value?.id !== undefined && view.value.id === route.params.viewId);

  return { showView, view };
};
